import React from 'react'
import { Navigate, useLocation, useMatch, useParams } from 'react-router'
import { useAuthUser } from 'contexts/AuthUserProvider'
import { loginURL, practiceWelcomeURL, practiceContractAgreementURL, practiceSignupURL, homeURL, leadDetailURL } from './urls'
import { useAPI } from 'contexts/APIProvider'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import Practice from 'logic/Practice'
import Lead from 'logic/Lead'

const IsPractice: React.FC = ({ children }) => {
  const params = useParams()
  const location = useLocation()
  const { api } = useAPI()
  const { authUser, logout } = useAuthUser()
  const { showAlert } = useSnackBarAlert()
  const [practice, setPractice] = React.useState<Practice>()
  const [lead, setLead] = React.useState<Lead | null>()  // undefined means not fetched yet, null means no lead
  const match = useMatch({ path: '/practice/:practice_id/:rest', caseSensitive: false, end: false })

  const practiceId = React.useMemo(() => Number(params.practiceId), [params.practiceId]) 

  const fetchPractice = React.useCallback(async () => {
    if (authUser?.practiceIds.includes(practiceId)) {
      setPractice(await api.getPractice(practiceId))
    }
  }, [api, authUser, practiceId])

  const fetchLead = React.useCallback(async () => {
    if (practice) {
      if (practice.lead_id) {
        try {
          setLead(await api.getLead(practice.lead_id, ['num_checks']))
        } catch (e) {
          showAlert('error', 'Failed to load lead information. Please try again.')
          setLead(null)
        }
      } else {
        setLead(null)
      }
    }
  }, [api, practice, showAlert])

  React.useEffect(() => {
    fetchPractice()
  }, [fetchPractice]) 

  React.useEffect(() => {
    fetchLead()
  }, [fetchLead])

  if (authUser === undefined) {
    return null
  } else if (authUser === null) {
    return <Navigate replace to={loginURL(location.pathname)} />
  } else if (authUser.isStaff) {
    return <React.Fragment>{children}</React.Fragment>
  } else if (authUser.practiceIds.includes(practiceId)) {
    if (practice === undefined || lead === undefined) {
      return null
    } else if (!practice.agreed_contracts && match?.params.rest !== 'contracts') {
      return <Navigate replace to={practiceContractAgreementURL(practiceId)} />
    } else if (!practice.enabled && match?.params.rest !== 'welcome') {
      return <Navigate replace to={practiceWelcomeURL(practiceId)} />
    } else if (lead && (practice.force_lead_signup || lead.num_checks_waiting_nurse > 0 || lead.num_checks_waiting_confirmation > 0)) {
      return <Navigate replace to={leadDetailURL(lead.id)} />
    } else if (!practice.practice_group_id && !practice.is_stripe_ready) {
      if (location.pathname.startsWith(practiceSignupURL()) 
        || match?.params.rest === 'billing'
        || match?.params.rest === 'setup-billing-account') {
        return <React.Fragment>{children}</React.Fragment>
      } else {
        return <Navigate replace to={practiceSignupURL()} />
      }
    }
    return <React.Fragment>{children}</React.Fragment>
  } else {
    const redirectURL = homeURL(authUser)
    if (redirectURL) {
      return <Navigate replace to={redirectURL} />
    } else {
      logout()
      return <Navigate replace to={loginURL()} />
    }
  }
}

export default IsPractice